/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { Button, IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as Msdyn365 from '@msdyn365-commerce/core';
import { IProductFeatureData } from './product-feature.data';
import { IProductFeatureProps } from './product-feature.props.autogenerated';

export interface IProductFeatureViewProps extends IProductFeatureProps<IProductFeatureData> {
    productFeature: IModuleProps;
    productFeatureContainer: INodeProps;
    productFeatureGrid: React.ReactNode;
}

/**
 *
 * ProductFeature component
 * @extends {React.PureComponent<IProductFeatureProps<IProductFeatureData>>}
 */
class ProductFeature extends React.PureComponent<IProductFeatureProps<IProductFeatureData>> {
    private _renderProductFeatureGrid = (props: IProductFeatureProps<IProductFeatureData>): React.ReactNode => {
        const { featureProductHeader, featureProductDescription, featureProductLink, featureProductImage } = props.config;
        if (!featureProductHeader) {
            return null;
        }
        const tag = (featureProductHeader?.tag ?? 'h2') as keyof JSX.IntrinsicElements;
        const Tag = tag;
        return (
            <>
                <>
                    <div className='product-feature-content'>
                        <div className='product-feature-text'>
                            <h2 className='product-feature-title'>
                                <Tag>{featureProductHeader ? featureProductHeader.text : ''}</Tag>
                            </h2>
                            <Msdyn365.RichTextComponent className='product-feature-description' text={featureProductDescription ?? ''} />
                            <Button
                                className='product-feature-button'
                                href={featureProductLink?.linkUrl.destinationUrl ?? ''}
                                title={featureProductLink?.linkText ?? ''}
                            >
                                {featureProductLink?.linkText ?? ''}
                            </Button>
                        </div>
                        <div className='product-feature-image'>
                            <img src={featureProductImage?.src} alt={featureProductImage?.altText} className='product-image' />
                        </div>
                    </div>
                </>
            </>
        );
    };
    public render(): JSX.Element | null {
        const viewProps: IProductFeatureViewProps = {
            ...(this.props as IProductFeatureProps<IProductFeatureData>),
            ...this.state,
            productFeature: {
                moduleProps: this.props,
                className: classnames('product-feature')
            },
            productFeatureContainer: {
                moduleProps: this.props,
                className: classnames('product-feature-container')
            },
            productFeatureGrid: this._renderProductFeatureGrid(this.props)
        };
        return this.props.renderView(viewProps) as React.ReactElement;
    }
}

export default ProductFeature;
